import { Component, inject, OnInit } from '@angular/core';

import { CommonModule, DatePipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { ActivatedRoute, Router } from '@angular/router';
import { PurchaseService } from '../../features/purchase/services/purchase.service';
import { PAYMENT_MODES } from '../../helpers/helper-file';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { MatDialog } from '@angular/material/dialog';
import { UploadFileService } from '../../shared/services/file-upload.service';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-purchase-new',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './purchase-new.component.html',
  styleUrl: './purchase-new.component.scss',
})
export class PurchaseNewComponent implements OnInit {
  purchaseNewForm: FormGroup;
  private purchaseService = inject(PurchaseService);
  public suppliersData: any[] | null = null;
  public stationaryData: any[] | null = null;
  paymentModeList: any = PAYMENT_MODES;
  public addedItems: any[] = [];
  dailyExpenseData: any;
  dailyExpenseId: any = null;

  readonly dialog = inject(MatDialog);
  file: any;
  fileUrl: any = '';
  // pdfFile: File[] = [];
  pdfFile: { file: File; url?: string }[] = [];
  url: string = '';

  suplierSearchCntrl = new FormControl();
  stationarySearchCntrl = new FormControl();
  search: any;
  searchStationay: any;
  today = new Date();
  grandTotal: number = 0;

  constructor(
    private fb: FormBuilder,
    private route: Router,
    private toastr: ToastrService,
    private router: ActivatedRoute,
    private datePipe: DatePipe,
    private documentUploadService: UploadFileService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.fetchAllSuppliers();
    this.fetchStationaries();
    this.router.paramMap.subscribe((params) => {
      this.dailyExpenseId = params.get('id');
      if (this.dailyExpenseId) {
        this.fetchDailyExpenseById(Number(this.dailyExpenseId));
      }
    });
    
    this.suplierSearchCntrl.valueChanges
    .pipe(
      debounceTime(300),
      distinctUntilChanged()
    )
    .subscribe((searchText) => {
      this.search = searchText;
      this.fetchAllSuppliers(searchText);
    });

    this.stationarySearchCntrl.valueChanges
    .pipe(
      debounceTime(300),
      distinctUntilChanged()
    )
    .subscribe((searchText) => {
      this.searchStationay = searchText;
      this.fetchStationaries(searchText);
    });

    this.purchaseNewForm.get('stationaryName')!.valueChanges.subscribe((selectedId) => {
      if (this.stationaryData) {
        const selectedItem = this.stationaryData.find(item => item.id === selectedId);
        if (selectedItem) {
          this.purchaseNewForm.get('unitPrice')!.setValue(selectedItem.unitPrice);
        } else {
          this.purchaseNewForm.get('unitPrice')!.reset();
        }
      }
    });
    
  
  }

  initForm(): void {
    this.purchaseNewForm = this.fb.group({
      id: [''],
      supplierName: ['', Validators.required],
      stationaryName: [''],
      billNumber: ['', Validators.required],
      quantity: ['', Validators.pattern(/^\d+(\.\d{1,2})?$/)],
      unitPrice: ['', Validators.pattern(/^\d+(\.\d{1,2})?$/)],
      // billDate: ['', Validators.required],
      billDate: [this.today, Validators.required], 
      paymentMethod: ['', Validators.required],
      chequeNo: [''],
      chequeDate: [''],
      fileUploadUrl: [''],
    });
    this.setupChequeValidation();
  }

  setupChequeValidation(): void {
    const chequeNoControl = this.purchaseNewForm.get('chequeNo');
    const chequeDateControl = this.purchaseNewForm.get('chequeDate');
    const paymentMethodControl = this.purchaseNewForm.get('paymentMethod');
  
    paymentMethodControl?.valueChanges.subscribe(method => {
      if (method === 'Cheque') {
        chequeNoControl?.setValidators([Validators.required]);
        chequeDateControl?.setValidators([Validators.required]);
      } else {
        chequeNoControl?.clearValidators();
        chequeDateControl?.clearValidators();
        // Clear the fields when not Cheque
        chequeNoControl?.reset();
        chequeDateControl?.reset();
      }
      chequeNoControl?.updateValueAndValidity();
      chequeDateControl?.updateValueAndValidity();
    });
  }
  
  allowOnlyDecimal(event: KeyboardEvent): void {
    const allowedKeys = ['0','1','2','3','4','5','6','7','8','9','.'];
    const inputChar = event.key;
  
    if (!allowedKeys.includes(inputChar)) {
      event.preventDefault();
      return;
    }
  
    // prevent more than one decimal point
    const input = event.target as HTMLInputElement;
    if (inputChar === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }

  allowOnlyInteger(event: KeyboardEvent): void {
    const inputChar = event.key;
    if (!/^[0-9]$/.test(inputChar)) {
      event.preventDefault();
    }
  }
  
  blockInvalidPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    const regex = /^[1-9]\d*$/;
    if (!regex.test(pastedInput)) {
      event.preventDefault();
    }
  }

  fetchAllSuppliers(search = '') {
    this.purchaseService.fetchSuppliers(search).subscribe({
      next: (data) => {
        this.suppliersData = data.map((supplier: any) => ({
          id: supplier?.id,
          supplierName: supplier?.supplierName || '--',
        }));
      },
      error: (error) => {
        console.error('Error fetching suppliers:', error);
      },
    });
  }

  fetchStationaries(search = '') {
    this.purchaseService.fetchStationaryItems(search).subscribe({
      next: (data) => {
        this.stationaryData = data.map((stationary: any) => ({
          id: stationary?.id,
          itemName: stationary?.stationoryName || '--',
          unitPrice: stationary?.price,
        }));
      },
      error: (error) => {
        console.error('Error fetching stationaries:', error);
      },
    });
  }

  addItem() {
    const itemId = this.purchaseNewForm.get('stationaryName')?.value;
    const quantity = this.purchaseNewForm.get('quantity')?.value;
    const unitPrice = this.purchaseNewForm.get('unitPrice')?.value;
    console.log(itemId);

    if (!itemId || !quantity || !unitPrice) {
      this.toastr.error('Please fill all required fields before adding.');
      return;
    }

    const selectedItem = this.stationaryData?.find(
      (item) => item.id === itemId
    );

    if (!selectedItem) {
      this.toastr.error('Invalid item selected.');
      return;
    }

    const existingItem = this.addedItems.find((item) => item.id === itemId);
    if (existingItem) {
      this.toastr.warning('Item already added!');
      setTimeout(() => {
        this.purchaseNewForm.patchValue({
          stationaryName: '',
          quantity: '',
          unitPrice: '',
        });
      }, 1000);
      return;
    }

    const subTotal = parseFloat(unitPrice) * parseFloat(quantity);
    const tax = subTotal * 0.05; // 5% Tax
    const total = subTotal + tax;

    

    this.addedItems.push({
      id: itemId,
      itemName: selectedItem.itemName,
      unitPrice: parseFloat(unitPrice),
      quantity: parseFloat(quantity),
      subTotal,
      tax,
      total,
    });

    this.grandTotal += total;
    this.toastr.success('Item added successfully!');


    this.purchaseNewForm.patchValue({
      stationaryName: '',
      quantity: '',
      unitPrice: ''
    });
  }

  
  fetchDailyExpenseById(id: any) {
    this.purchaseService.fetchDailyExpenseById(id).subscribe({
      next: (data) => {
        this.dailyExpenseData = data;
        console.log('Fetched Data:', data);
        this.grandTotal = data.total;

        // Patch form values
        const formattedDate = this.datePipe.transform(data.date, 'yyyy-MM-dd');
        this.purchaseNewForm.patchValue({
          supplierName: data.supplierId,
          billNumber: data.dailyExpenseNo,
          billDate: formattedDate,
          paymentMethod: data.paymentMethod,
          chequeNo: data.chequeNo,
          chequeDate: data.chequeDate,
          // fileUploadUrl: data.fileUploadUrl,
        });

        this.addedItems = data.dailyExpenseItemsOfExpense.map((item: any) => ({
          id: item.stationoryId,
          itemName: item.stationoryData?.stationoryName || '--',
          unitPrice: parseFloat(item.unitPrice),
          quantity: parseFloat(item.quantity),
          subTotal: (
            parseFloat(item.unitPrice) * parseFloat(item.quantity)
          ).toFixed(2),
          tax: parseFloat(item.tax).toFixed(2),
          total: parseFloat(item.total).toFixed(2),
        }));

        console.log('Populated Items:', this.addedItems);

        // if (this.addedItems.length > 0) {
        //   const firstItem = this.addedItems[0];
        //   this.purchaseNewForm.patchValue({
        //     stationaryName: firstItem.id,
        //     quantity: firstItem.quantity,
        //     unitPrice: firstItem.unitPrice,
        //   });
        // }
      },
      error: (error) => {
        console.error('Error fetching daily expense:', error);
      },
    });
  }

  // removeItem(itemId: string) {
  //   this.addedItems = this.addedItems.filter((item) => item.id !== itemId);
  //   this.toastr.success('Item removed successfully.');
  // }

  removeItem(itemId: string) {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete Item ?`,
        confirmText: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const itemToRemove = this.addedItems.find(item => item.id === itemId);
        if (itemToRemove) {
          this.grandTotal -= itemToRemove.total;
        }
        this.addedItems = this.addedItems.filter((item) => item.id !== itemId);
        this.toastr.success('Item removed successfully.');
      }
    });
  }

  onCancel() {
    this.purchaseNewForm.reset();
    this.route.navigate(['/daily-purchase']);
  }

  removeFile() {
    this.pdfFile = [];
    this.purchaseNewForm.patchValue({
      fileUploadUrl: '',
    });
  }
  onViewDocument(file: any) {
    const token = localStorage.getItem('AUTH_TOKEN');
    const url = file?.url?.url;
    fetch(url, {
      method: 'GET',
      headers: {
        authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch((error) => console.error('Error:', error));
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate PDF format
      if (file.type !== 'application/pdf') {
        this.toastr.error('Only PDF files are allowed.');
        return;
      }

      // Clear any previously uploaded files
      this.pdfFile = [];

      // Upload the selected file
      this.uploadDocuments(file);
    }
  }

  uploadDocuments(file: File) {
    console.log('Uploading file:', file);

    this.documentUploadService.uploadDocument(file, 'expensebill').subscribe({
      next: (response) => {
        // Extract the correct URL from the response
        const fileUrl = response?.url ? response.url : response;

        // Store the URL
        this.url = fileUrl;

        // Ensure pdfFile array stores only the file metadata
        this.pdfFile = [{ file, url: fileUrl }];

        // Patch form with the correct file URL
        this.purchaseNewForm.patchValue({
          fileUploadUrl: fileUrl, // Ensures only the URL is stored
        });

        console.log('Uploaded file URL:', fileUrl);
      },
      error: (error) => {
        console.log(error);
        this.toastr.error('File upload failed');
      },
    });
  }

  onSubmit() {
    if (this.purchaseNewForm.invalid || this.addedItems.length === 0) {
      this.toastr.error(
        'Please fill in all required fields and add at least one item.'
      );
      return;
    }

    const createDailyExpenseInput = {
      supplierId: parseInt(this.purchaseNewForm.get('supplierName')?.value, 10),
      date: this.purchaseNewForm.get('billDate')?.value,
      dailyExpenseNo: this.purchaseNewForm.get('billNumber')?.value,
      paymentMethod: this.purchaseNewForm.get('paymentMethod')?.value,
      tax: this.addedItems.reduce((acc, item) => acc + parseFloat(item.tax), 0),
      total: this.addedItems.reduce(
        (acc, item) => acc + parseFloat(item.total),
        0
      ),
      subTotal: this.addedItems.reduce(
        (acc, item) => acc + parseFloat(item.subTotal),
        0
      ),
      fileUploadUrl: this.purchaseNewForm.get('fileUploadUrl')?.value || '',
      chequeNo: this.purchaseNewForm.get('chequeNo')?.value || '',
      chequeDate: this.purchaseNewForm.get('chequeDate')?.value || '',
    };

    const createDailyExpenseInputUpdate = {
      supplierId: parseInt(this.purchaseNewForm.get('supplierName')?.value, 10),
      date: this.purchaseNewForm.get('billDate')?.value,
      dailyExpenseNo: this.purchaseNewForm.get('billNumber')?.value,
      paymentMethod: this.purchaseNewForm.get('paymentMethod')?.value,
      tax: this.addedItems.reduce((acc, item) => acc + parseFloat(item.tax), 0),
      total: this.addedItems.reduce(
        (acc, item) => acc + parseFloat(item.total),
        0
      ),
      subTotal: this.addedItems.reduce(
        (acc, item) => acc + parseFloat(item.subTotal),
        0
      ),
      chequeNo: this.purchaseNewForm.get('chequeNo')?.value || '',
      chequeDate: this.purchaseNewForm.get('chequeDate')?.value || '',
    };

    const createDailyExpenseItemsInputs = this.addedItems.map((item) => ({
      stationoryId: parseInt(item.id, 10),
      quantity: parseFloat(item.quantity),
      unitPrice: parseFloat(item.unitPrice),
      tax: parseFloat(item.tax),
      total: parseFloat(item.total),
    }));

    const updateDailyExpenseItemInputs = this.addedItems.map((item) => {
      const matchedExpenseItem =
        this.dailyExpenseData?.dailyExpenseItemsOfExpense?.find(
          (expenseItem: any) => expenseItem.stationoryId === item.id
        );

      return {
        ...(matchedExpenseItem?.id ? { id: matchedExpenseItem.id } : {}),
        stationoryId: parseInt(item.id, 10),
        quantity: parseFloat(item.quantity),
        unitPrice: parseFloat(item.unitPrice),
        tax: parseFloat(item.tax),
        total: parseFloat(item.total),
      };
    });

    if (!this.dailyExpenseId) {
      this.purchaseService
        .createExpense(createDailyExpenseInput, createDailyExpenseItemsInputs)
        .subscribe({
          next: (response) => {
            this.toastr.success('Purchase successfully created!');
            this.route.navigate(['/daily-purchase']);
          },
          error: (error) => {
            console.error('Error submitting purchase:', error);
            this.toastr.error('Failed to create purchase. Please try again.');
          },
        });
    } else {
      const updateDailyExpenseInput = {
        id: Number(this.dailyExpenseId),
        ...createDailyExpenseInputUpdate,
      };
      // const updateDailyExpenseItemsInputs = {
      //   ...createDailyExpenseItemsInputs,
      // };
      this.purchaseService
        .updateDailyExpense(
          updateDailyExpenseInput,
          updateDailyExpenseItemInputs
        )
        .subscribe({
          next: (data) => {
            this.toastr.success('Expense updated successfully', 'Success');
            this.route.navigate(['/daily-purchase']);
          },
          error: (error) => {
            this.toastr.error(error, 'Error');
          },
        });
    }
  }

  onSupplierSearch(event: MatSelectChange) {
    const supplierID = event.value;
    this.purchaseNewForm.get('supplierName')?.setValue(supplierID);
    // this.fetchAllSuppliers(supplierID);
  }

  onStationarySearch(event: MatSelectChange) {
    const stationaryID = event.value;
    this.purchaseNewForm.get('stationaryName')?.setValue(stationaryID);
    // this.fetchStationaries(stationaryID);
  }



}
